/**
 * DEV-1857 (эпик DEV-1850). Маячок просмотра основного сайта.
 *
 * До него в отчёте по трафику была строка на каждый лендинг и ни одной — на
 * vinprop.vn. Кампанию на главную завести было можно, а увидеть, сколько она
 * привела, — нет: просмотры считает приёмник landings, и стучались в него
 * только лендинги.
 *
 * Сайт отправляет то же событие и тем же путём, что и лендинги, со слагом
 * `site`: для аналитики это обычная площадка, и отдельной ветки в отчётах не
 * появляется.
 */
import { ApiService, buildApiUrl } from '@/config/api.config'

/**
 * Слаг основного сайта в фактах аналитики. Один и тот же у маячка и у заявки:
 * разъехавшись, они дали бы в отчёте две строки — просмотры без заявок и
 * заявки без просмотров.
 */
export const SITE_SLUG = 'site'

/**
 * Метки, которые уезжают с событием. Именно белый список, а не весь query:
 * приёмник публичный, и произвольные параметры страницы попадали бы прямо в
 * fact-таблицу. Набор совпадает с тем, что пропускает приёмник — то, что не
 * совпало, он всё равно отбросит молча.
 */
const ATTRIBUTION_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
  'fbclid',
  'yclid',
  'ttclid',
] as const

/** Пределы приёмника (landings/internal/service/landings/views.go). */
const MAX_REFERRER = 512
const MAX_UTM_VALUE = 256

export interface SiteViewEvent {
  landing_slug: string
  referrer?: string
  utm?: Record<string, string>
}

/**
 * buildSiteViewEvent собирает событие просмотра из адреса страницы.
 *
 * Значения режутся по пределам приёмника: длинную метку он всё равно обрежет,
 * но обрезанная здесь и обрезанная там дают одну и ту же строку отчёта.
 */
export function buildSiteViewEvent(location: Location | URL, referrer: string): SiteViewEvent {
  const event: SiteViewEvent = { landing_slug: SITE_SLUG }

  if (referrer) {
    event.referrer = referrer.slice(0, MAX_REFERRER)
  }

  const params = new URLSearchParams(location.search || '')
  const utm: Record<string, string> = {}

  for (const key of ATTRIBUTION_KEYS) {
    const value = params.get(key)
    if (value) {
      utm[key] = value.slice(0, MAX_UTM_VALUE)
    }
  }

  if (Object.keys(utm).length > 0) {
    event.utm = utm
  }

  return event
}

/**
 * isPresentationPath — обезличенный лендинг презентации агента (/p/:token,
 * DEV-1015). Он живёт вне брендинга сайта, у него свой счётчик и чужие метки.
 *
 * Проверяем по адресу, а не по имени роута: приложение монтируется без
 * router.isReady(), и на первом onMounted имени роута может ещё не быть — тогда
 * маячок ушёл бы и отсюда.
 */
export function isPresentationPath(pathname: string): boolean {
  return pathname === '/p' || pathname.startsWith('/p/')
}

/**
 * isPresentationUrl — то же по полному адресу страницы. Заявка знает, откуда её
 * отправили, и помечать её слагом сайта нужно именно по этому адресу, а не по
 * тому, где браузер оказался к моменту отправки.
 */
export function isPresentationUrl(url: string): boolean {
  try {
    return isPresentationPath(new URL(url, 'https://vinprop.vn').pathname)
  } catch {
    return false
  }
}

/**
 * recordSiteView отправляет маячок. Ошибки проглатываются: счётчик просмотров —
 * не та причина, по которой страница должна ругаться в консоль посетителю.
 *
 * keepalive — чтобы переход по ссылке сразу после загрузки не отменял запрос.
 */
export async function recordSiteView(): Promise<void> {
  if (isPresentationPath(window.location.pathname)) {
    return
  }

  try {
    await fetch(buildApiUrl(ApiService.Landings, 'landings/views'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(buildSiteViewEvent(window.location, document.referrer)),
      keepalive: true,
    })
  } catch {
    // Приёмник недоступен — посетителю это знать незачем.
  }
}
